<template>
	<Dialog :visible="true" style="width: 55vw" modal :closable="false">
		<template #header>
			<div class="flex justify-between w-full">
				<h1>{{ pageEditText }}</h1>
				<div class="flex gap-2">
					<Button id="btnBackToPRList" class="btn-tiny" @click="emit('cancel')" :disabled="spinnerRuns">Cancel</Button>

					<Button @click="save" class="btn-tiny" :disabled="spinnerRuns" v-show="SessionInfo.hasRightsToScreen('Assessments', PageAccess.Create)">
						<template #default>
							<span v-if="spinnerRuns">
								<FontAwesomeIcon :icon="['fa', 'spinner']" spin class="mr-2" />
								Saving...
							</span>
							<span v-else> Save </span>
						</template>
					</Button>
				</div>
			</div>
		</template>

		<I2FormRow>
			<I2FormField label="Assessment Name">
				<InputText
					v-model="assessment.assessmentName"
					:class="{ 'p-invalid': saveAttempted && !assessment.assessmentName, '': assessment.assessmentName !== undefined }"
					maxlength="200"
				/>
			</I2FormField>
		</I2FormRow>

		<I2FormRow>
			<!-- <I2FormField label="Assessment Number">
				<b>{{ assessment.asessementIncrementalNumber }}</b>
			</I2FormField> -->
			<I2FormField>
				<template #label>
					<span class="flex items-center gap-2">
						Development Stage
						<i
							v-if="isDevelopmentStageInactive"
							class="pi pi-exclamation-triangle text-yellow-500"
							v-tooltip="'This development stage is no longer active and cannot be selected in new assessments'"
						></i>
					</span>
				</template>
				<Select
					v-model="selectedDevelopmentStage"
					:options="developmentStageList"
					optionLabel="valueTxt"
					placeholder="Select a Development Stage"
					class="w-full md:w-14rem"
					:class="{ 'p-invalid': saveAttempted && selectedDevelopmentStage == null, '': selectedDevelopmentStage !== null }"
					:filter="developmentStageList.length > 10"
				>
				</Select>
			</I2FormField>
		</I2FormRow>
		<I2FormRow>
			<I2FormField>
				<template #label>
					Electronic Lab Number (ELN)
					<button
						@click.prevent="EventBus.emit('OpenPageHelp', { helpPageName: 'AssessmentElectronicLabNumber', userFriendlyName: 'Electronic Lab Number (ELN)' })"
						v-tooltip.top="'View Field Help'"
						class="ml-1"
						style="background: none; border: none; padding: 0; cursor: pointer"
					>
						<i class="pi pi-info-circle text-blue-500"></i>
					</button>
				</template>
				<InputText v-model="assessment.electronicLabNumber" maxlength="200" />
			</I2FormField>
			<I2FormField>
				<template #label>
					<span class="flex items-center gap-2">
						Product Name
						<i
							v-if="isProductNameInactive"
							class="pi pi-exclamation-triangle text-yellow-500"
							v-tooltip="'This product is no longer active and cannot be selected in new assessments'"
						></i>
					</span>
				</template>
				<span v-if="addNewProductName">
					<div class="flex items-center space-x-4">
						<InputText
							v-model="newProductName"
							placeholder="Insert a Product Name"
							class="w-full md:w-14rem"
							:class="{ 'p-invalid': saveAttempted && newProductName == null, '': newProductName !== null }"
							filter
						/>
						<Button id="btnSaveNewProductName" type="button" @click="actionOnProductName('save')" class="btn bg-redSignal">Save</Button>
						<Button id="btnCancelAddNewProductName" type="button" @click="actionOnProductName('cancel')" class="btn bg-redSignal">Cancel</Button>
					</div>
				</span>
				<span v-else>
					<div class="flex items-center space-x-4">
						<Select
							v-model="selectedProductName"
							:options="productNameList"
							optionLabel="valueTxt"
							placeholder="Select a Product Name"
							class="w-full md:w-14rem"
							:class="{ 'p-invalid': saveAttempted && selectedProductName == null, '': selectedProductName !== null }"
							:filter="productNameList.length > 10"
						/>
						<Button
							v-show="addNewProductEnabled"
							id="btnAddNewProductName"
							type="button"
							@click="actionOnProductName('add')"
							class="btn bg-redSignal"
						>
							Add
						</Button>
					</div>
				</span>
			</I2FormField>
		</I2FormRow>
		<I2FormRow>
			<I2FormField label="Assessment Collaborators">
				<AutoComplete
					:multiple="true"
					v-model="selectedPersons"
					:suggestions="filteredPersons"
					:optionLabel="personLabel"
					optionValue="id"
					@complete="searchPerson($event)"
					:field="personLabel"
					@item-select="checkIfPersonIsPresent($event)"
				/>
			</I2FormField>
		</I2FormRow>
		<I2FormRow>
			<I2FormField v-if="psiDevDashboardEnabled" style="justify-content: flex-start">
				<span class="flex items-center gap-2">
					<Checkbox v-model="assessment.includeInPsiDevDashboard" :binary="true" inputId="includeInPsiDevDashboardCheckbox" />
					<label for="includeInPsiDevDashboardCheckbox">Include in PSI-Dev Dashboard</label>
				</span>
			</I2FormField>
			<I2FormField label="State">
				<span v-tooltip="canEditState ? undefined : 'This field can be edited by client admin'">
					<Select
						v-model="assessment.state"
						:options="stateOptions"
						placeholder="Select a State"
						class="w-full md:w-14rem"
						:disabled="!canEditState"
						showClear
					/>
				</span>
			</I2FormField>
		</I2FormRow>
		<I2FormRow>
			<I2FormField label="Assessment Comments">
				<Textarea v-model="assessment.comments" rows="4" />
			</I2FormField>
		</I2FormRow>
		<I2FormRow right> </I2FormRow
	></Dialog>
	<Toast position="top-right" />
</template>
<script setup lang="ts">
import { ref, onMounted, reactive, computed } from "vue"
import {
	ApiClient,
	Person,
	AssessmentCollaboratorPut,
	Assessment,
	MasterDropdown,
	DevelopmentStage,
	ConsumerEndOfLifeDropdowns,
	FeatureFlagClient,
} from "../../entities/NSwagGenerated"
import { SessionInfo, PageAccess } from "../auth/personSessionData"
import Textarea from "primevue/textarea"
import Dialog from "primevue/dialog"
import AutoComplete, { AutoCompleteCompleteEvent } from "primevue/autocomplete"
import InputText from "primevue/inputtext"
import Button from "primevue/button"
import Checkbox from "primevue/checkbox"
import Select from "primevue/select"
import PageLabel from "./pageLabel.vue"
import * as Session from "../auth/personSessionData"
import { useToast } from "primevue/usetoast"
import { library } from "@fortawesome/fontawesome-svg-core"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { faSpinner } from "@fortawesome/free-solid-svg-icons"
import { EventBus } from "@/eventBus"
library.add(faSpinner)
const apiClient = new ApiClient(import.meta.env.VITE_API_URL?.toString())
const saveAttempted = ref<boolean>(false)
const selectedPersons = ref<Person[] | undefined>([] as Person[])
const filteredPersons = ref<Person[] | undefined>([] as Person[])
const selectedProductName = ref<MasterDropdown | undefined>(undefined)
const selectedDevelopmentStage = ref<MasterDropdown | undefined>(undefined)
const developmentStageList = ref<MasterDropdown[]>([] as MasterDropdown[])
const productNameList = ref<MasterDropdown[]>([] as MasterDropdown[])
const assessmentNumber = ref<number | undefined>(undefined)

const newProductName = ref<string | undefined>(undefined)
const addNewProductName = ref<boolean>(false)
const addNewProductEnabled = ref<boolean>(false)
const spinnerRuns = ref<boolean>(false)
const psiDevDashboardEnabled = ref<boolean>(false)
const stateOptions = ["Draft", "Final"]
const featureFlagClient = new FeatureFlagClient(import.meta.env.VITE_API_URL?.toString())

const canEditState = computed(() => {
	const role = SessionInfo.currentUser().role
	return SessionInfo.currentUserIsAdmin() || role.indexOf("ERM Admin") !== -1 || role.indexOf("Client Admin") !== -1
})

const isDevelopmentStageInactive = computed(() => {
	if (!selectedDevelopmentStage.value) return false
	return developmentStageList.value.find((item) => item.id === selectedDevelopmentStage.value!.id && item.isActive === false) !== undefined
})

const isProductNameInactive = computed(() => {
	if (!selectedProductName.value) return false
	return productNameList.value.find((item) => item.id === selectedProductName.value!.id && item.isActive === false) !== undefined
})

let assessment = new Assessment()
let assessmentAndCollaborators = new AssessmentCollaboratorPut()
let pageEditText: string = ""
let toast = useToast()
const props = defineProps({
	assessment: { type: Assessment, required: true },
})
const emit = defineEmits<{
	(e: "cancel"): void
	(e: "save", assessmentNumber?: string): void
	(e: "deleted"): void
}>()

onMounted(async () => {
	// First set the selected values from props
	if (props.assessment.id === undefined) {
		pageEditText = "Create Assessment"
		assessment.id = self.crypto.randomUUID()
		assessment.isActive = true
		assessment.subProjectId = props.assessment.subProjectId

		selectedDevelopmentStage.value = undefined
		selectedProductName.value = undefined
		selectedPersons.value = [] as Person[]
	} else {
		pageEditText = "Modify Assessment"
		assessment = props.assessment
		assessmentNumber.value = assessment.assessmentNumber
		selectedDevelopmentStage.value = props.assessment.developmentStage
		selectedProductName.value = props.assessment.productName

		selectedPersons.value = props.assessment.assessmentCollaborators?.map((collaborator) => collaborator.person) as Person[]
	}

	// Then load the dropdown lists (they will check for inactive selected items)
	await Promise.all([getAllDevelopmentStageEntries(), getAllProductNameEntries(), loadPsiDevDashboardFlag()])
})

async function loadPsiDevDashboardFlag(): Promise<void> {
	try {
		psiDevDashboardEnabled.value = await featureFlagClient.isEnabled("Enable PSI-Dev Dashboard")
	} catch {
		psiDevDashboardEnabled.value = false
	}
}

const personLabel = (person: Person) => {
	return `${person.firstName} ${person.lastName}`
}

const actionOnProductName = (action: string) => {
	switch (action) {
		case "save":
			saveAttempted.value = true
			if (!testFieldsSaveProduct()) return
			var newProductNameTOSave = new MasterDropdown()
			newProductNameTOSave.id = self.crypto.randomUUID()
			newProductNameTOSave.valueTxt = newProductName.value
			newProductNameTOSave.valueNumeric = undefined
			newProductNameTOSave.label = "ProductName"
			newProductNameTOSave.help = newProductName.value
			newProductNameTOSave.ordering = productNameList.value.length
			apiClient.saveTheDropdownWithMasterCategoryKey(newProductNameTOSave).then(
				(res) => {
					getAllProductNameEntries().then(() => {
						selectedProductName.value = res
					})
				},
				(err) => {
					throw err
				}
			)
			addNewProductName.value = false
			break
		case "cancel":
			newProductName.value = undefined
			addNewProductName.value = false
			break
		case "add":
			saveAttempted.value = false
			newProductName.value = undefined
			addNewProductName.value = true
			break
	}
}

function getAllDevelopmentStageEntries(): Promise<void> {
	return new Promise((resolve, reject) => {
		apiClient
			.masterDropdownByCategoryKeyAll("DevelopmentStage")
			.then((res: any) => {
				// Filter to only active items
				const activeItems = res.filter((item: MasterDropdown) => item.isActive === true)
				// Check if selected item is inactive and add it back
				if (selectedDevelopmentStage.value) {
					const selectedItem = res.find((item: MasterDropdown) => item.id === selectedDevelopmentStage.value!.id)
					if (selectedItem && selectedItem.isActive === false && !activeItems.some((i: MasterDropdown) => i.id === selectedItem.id)) {
						activeItems.push(selectedItem)
					}
				}
				developmentStageList.value = activeItems
				resolve()
			})
			.catch((error) => {
				reject(error)
			})
	})
}
const getAllProductNameEntries = (): Promise<void> => {
	return new Promise((resolve, reject) => {
		apiClient
			.masterDropdownByCategoryKeyAll("ProductName")
			.then((res: any) => {
				// Filter to only active items
				const activeItems = res.filter((item: MasterDropdown) => item.isActive === true)
				// Check if selected item is inactive and add it back
				if (selectedProductName.value) {
					const selectedItem = res.find((item: MasterDropdown) => item.id === selectedProductName.value!.id)
					if (selectedItem && selectedItem.isActive === false && !activeItems.some((i: MasterDropdown) => i.id === selectedItem.id)) {
						activeItems.push(selectedItem)
					}
				}
				productNameList.value = activeItems
				resolve()
			})
			.catch((error) => {
				reject(error)
			})
	})
}
function checkIfPersonIsPresent(event: any) {
	if (selectedPersons.value === undefined) return
	let isPresent = selectedPersons.value.filter((person: any) => person.id === event.value.id)
	if (isPresent.length > 1) {
		toast.add({
			severity: "warn",
			summary: "The selected collaborator is already in the collaborator list for this Project",
			detail: `Select another collaborator`,
			life: 3000,
		})
		selectedPersons.value.splice(-1, 1)
	}
}

function testFieldsSaveProduct() {
	if (newProductName.value == undefined || newProductName.value == "") {
		toast.add({
			severity: "error",
			summary: "Error",
			detail: "You must compile the highlighted fields",
			life: 2500,
		})
		saveAttempted.value = true
		return false
	}

	return true
}

function testFields() {
	if (!assessment.assessmentName || !selectedDevelopmentStage.value || !selectedProductName.value) {
		toast.add({
			severity: "error",
			summary: "Error",
			detail: "You must compile the highlighted fields",
			life: 2500,
		})
		saveAttempted.value = true
		return false
	}

	return true
}
function save() {
	saveAttempted.value = true
	if (!testFields()) return
	spinnerRuns.value = true
	let assessmentAndCollaborators = new AssessmentCollaboratorPut()

	assessmentAndCollaborators.collaborators = selectedPersons.value
	assessmentAndCollaborators.assessment = assessment
	assessmentAndCollaborators.assessment.developmentStageId = selectedDevelopmentStage.value ? selectedDevelopmentStage.value.id : undefined
	assessmentAndCollaborators.assessment.productNameId = selectedProductName.value ? selectedProductName.value.id : undefined

	apiClient
		.postAssessmentCollaborators(assessmentAndCollaborators)
		.then((updatedAssessment) => {
			// Use the returned assessment data directly instead of making another request
			emit("save", updatedAssessment.asessementIncrementalNumber?.toString())
			spinnerRuns.value = false
		})
		.catch((error) => {
			console.error("Failed to save assessment:", error)
			emit("save")
			spinnerRuns.value = false
		})
}

function searchPerson(event: AutoCompleteCompleteEvent) {
	apiClient.filterPersonForCollaborators(event.query).then((res) => {
		filteredPersons.value = res
	})
}
</script>
<style scoped></style>
